import { useQuery } from '@tanstack/react-query';
import { isString } from 'lodash';

import { Subscriptions } from '@air/api';
import { type Subscription } from '@air/types';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export const SUBSCRIPTION = 'SUBSCRIPTION';

export const getSubscriptionKey = (workspaceId?: string) => {
  return workspaceId ? [SUBSCRIPTION, { workspaceId }] : [SUBSCRIPTION];
};

export type EnhancedSubscription = {
  hasActiveSubscription: boolean;
  isInFreePlan: boolean;
  isOnPlusPlan: boolean;
  isOnCreatorPlan: boolean;
  isOnStarterPlan: boolean;
  isOnProPlan: boolean;
  isOnBusinessPlan: boolean;
  isFixedModel: boolean;
  isPerMemberModel: boolean;
  isCreditModel: boolean;
  isEnterprise: boolean;
  isOnPaidPlan: boolean;
} & Subscription;

export const PRO_PLANS: Subscription['plan'][] = ['pro', 'business', 'demo', 'enterprise'];
export const PAID_PLANS: Subscription['plan'][] = [
  'creator',
  'starter',
  'plus',
  'pro',
  'business',
  'demo',
  'enterprise',
];

export const useSubscription = () => {
  const { currentWorkspace } = useCurrentWorkspace();
  const subscriptionKey = getSubscriptionKey(currentWorkspace?.id);

  return useQuery<EnhancedSubscription>({
    queryKey: subscriptionKey,

    queryFn: async () => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }
      const subscription = await Subscriptions.get({ workspaceId });

      return {
        ...subscription,
        /**
         * The backend is currently sending down a string for the usedStorage instead of a number sometimes (it has to do with caching).
         */
        usedStorage: isString(subscription.usedStorage) ? parseInt(subscription.usedStorage) : subscription.usedStorage,
        hasActiveSubscription: subscription.status === 'active',
        isFixedModel: subscription.pricingModel === 'fixed',
        isPerMemberModel: subscription.pricingModel === 'perMember',
        isInFreePlan: subscription.plan === 'basic',
        isOnCreatorPlan: subscription.plan === 'creator',
        isOnStarterPlan: subscription.plan === 'starter',
        isOnPlusPlan: subscription.plan === 'plus',
        isOnBusinessPlan: subscription.plan === 'business',
        isCreditModel: subscription.pricingModel === 'credit',
        isEnterprise: subscription.plan === 'enterprise',
        isOnProPlan: PRO_PLANS.includes(subscription.plan),
        isOnPaidPlan: PAID_PLANS.includes(subscription.plan),
      };
    },
    staleTime: Infinity,
    enabled: !!currentWorkspace?.id,
  });
};
